import { useState } from 'react';
import { useAuthFile } from './useAuthFile';

export function isImage(url: string) {
  return /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url);
}

interface AttachmentViewProps {
  url: string;
  fileName?: string;
  variant: 'bubble' | 'task' | 'thumb';
  mine?: boolean;
  thumbSize?: number;
}

const thumbBox = (size: number) => ({
  width: size,
  height: size,
  borderRadius: 8,
  background: 'var(--vkui--color_background_secondary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
});

const taskBox = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 72,
  maxWidth: 140,
  padding: '0 10px',
  borderRadius: 8,
  overflow: 'hidden',
  background: 'var(--vkui--color_background_secondary)',
  fontSize: 12,
  textDecoration: 'none',
  color: 'var(--vkui--color_text_primary)',
} as const;

function pillStyle(mine?: boolean) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 10,
    background: mine ? 'rgba(255,255,255,0.18)' : 'var(--vkui--color_background_accent)',
    color: mine ? 'var(--app-on-accent)' : 'var(--vkui--color_text_link)',
    fontSize: 13,
    textDecoration: 'none',
    wordBreak: 'break-all' as const,
  };
}

/** In-app fullscreen photo preview (VK webview cannot open external/blob links reliably). */
function PhotoPreview({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-label="Просмотр фото"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(20, 18, 22, 0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'zoom-out',
      }}
    >
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          width: 36,
          height: 36,
          borderRadius: 18,
          border: 'none',
          background: 'rgba(255,255,255,0.18)',
          color: '#fff',
          fontSize: 20,
          lineHeight: '36px',
          cursor: 'pointer',
        }}
      >
        ×
      </button>
      <img
        src={src}
        alt=""
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '92vw', maxHeight: '86vh', borderRadius: 8, cursor: 'default', display: 'block' }}
      />
    </div>
  );
}

export function AttachmentView({ url, fileName, variant, mine, thumbSize = 56 }: AttachmentViewProps) {
  const blobUrl = useAuthFile(url);
  const [preview, setPreview] = useState(false);
  const name = fileName || url.split('/').pop() || '';
  const image = isImage(url);
  const zoom = { cursor: 'zoom-in' as const };

  const overlay = preview && blobUrl && image ? <PhotoPreview src={blobUrl} onClose={() => setPreview(false)} /> : null;

  if (variant === 'thumb') {
    if (image && blobUrl) {
      return (
        <>
          <img
            src={blobUrl}
            alt=""
            onClick={() => setPreview(true)}
            style={{ width: thumbSize, height: thumbSize, objectFit: 'cover', borderRadius: 8, ...zoom }}
          />
          {overlay}
        </>
      );
    }
    return <div style={thumbBox(thumbSize)}>📎</div>;
  }

  if (variant === 'task') {
    if (image && blobUrl) {
      return (
        <>
          <img
            src={blobUrl}
            alt=""
            onClick={() => setPreview(true)}
            style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, display: 'block', ...zoom }}
          />
          {overlay}
        </>
      );
    }
    return blobUrl ? (
      <a href={blobUrl} download={name} style={taskBox}>📎 Файл</a>
    ) : (
      <div style={taskBox}>📎 Файл</div>
    );
  }

  if (image && blobUrl) {
    return (
      <>
        <img
          src={blobUrl}
          alt=""
          onClick={() => setPreview(true)}
          style={{ maxWidth: 200, maxHeight: 200, borderRadius: 10, display: 'block', objectFit: 'cover', ...zoom }}
        />
        {overlay}
      </>
    );
  }
  return blobUrl ? (
    <a href={blobUrl} download={name} style={pillStyle(mine)}>📎 {name}</a>
  ) : (
    <span style={pillStyle(mine)}>📎 {name}</span>
  );
}
